import React, { useState, useRef, useCallback } from 'react';

const THRESHOLD = 70;
const MAX_PULL = 100;

const PullToRefresh = ({ onRefresh, children }) => {
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const startY = useRef(0);
  const currentPull = useRef(0);
  const isTracking = useRef(false);

  const handleTouchStart = useCallback((e) => {
    if (window.scrollY > 0 || isRefreshing) return;
    startY.current = e.touches[0].clientY;
    isTracking.current = true;
  }, [isRefreshing]);

  const handleTouchMove = useCallback((e) => {
    if (!isTracking.current || isRefreshing) return;
    const delta = e.touches[0].clientY - startY.current;
    if (delta <= 0) {
      currentPull.current = 0;
      setPullDistance(0);
      isTracking.current = false;
      return;
    }
    // Only intercept if user is pulling down from the very top
    if (window.scrollY > 0) {
      isTracking.current = false;
      setPullDistance(0);
      return;
    }
    const eased = Math.min(delta * 0.5, MAX_PULL);
    currentPull.current = eased;
    setPullDistance(eased);
  }, [isRefreshing]);

  const handleTouchEnd = useCallback(async () => {
    if (!isTracking.current) return;
    isTracking.current = false;

    if (currentPull.current >= THRESHOLD && onRefresh && !isRefreshing) {
      setIsRefreshing(true);
      setPullDistance(THRESHOLD);
      try {
        await onRefresh();
      } catch (err) {
        console.error('Pull to refresh error:', err);
      } finally {
        setIsRefreshing(false);
        setPullDistance(0);
      }
    } else {
      setPullDistance(0);
    }
    currentPull.current = 0;
  }, [onRefresh, isRefreshing]);

  const progress = Math.min(pullDistance / THRESHOLD, 1);

  return (
    <div
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{ position: 'relative', touchAction: 'pan-y' }}
    >
      {/* Pull indicator */}
      <div
        className="flex items-center justify-center overflow-hidden transition-opacity"
        style={{
          height: `${pullDistance}px`,
          opacity: pullDistance > 0 ? 1 : 0,
        }}
      >
        <div
          className={`flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 ${isRefreshing ? 'animate-spin' : ''}`}
          style={{
            transform: `rotate(${progress * 360}deg)`,
            transition: isRefreshing ? 'none' : 'transform 0.2s ease-out',
          }}
        >
          <svg
            className="w-4 h-4 text-primary"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            style={{
              transform: progress >= 1 ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease',
            }}
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
        </div>
      </div>

      {children}
    </div>
  );
};

export default PullToRefresh;
